import { replayIntegration, setUser as setSentryUser } from '@sentry/browser';
import { browserTracingIntegration, init as initSentry, setTag } from '@sentry/react';
import * as Sentry from '@sentry/react';

import { isCI, isDev } from 'src/common/helpers/EnvironmentHelper';
import { ITenant } from 'src/common/interfaces/Tenant';
import { IUser } from 'src/common/interfaces/User';
import { Logger } from 'src/common/services/Logger';

type LoggerMessage = { msg: unknown; metadata?: Record<string, string> };

const PRODUCT_NAME = 'platform';
let isCapturingSentryLoggerMessage = false;

const serializeSentryExtra = (value: unknown): unknown => {
	if (value instanceof Error) {
		return {
			message: value.message,
			name: value.name,
			stack: value.stack
		};
	}

	if (typeof value === 'object' && value !== null) {
		try {
			return JSON.parse(JSON.stringify(value));
		} catch {
			return String(value);
		}
	}

	return value;
};

const stringifyLoggerArg = (value: unknown): string => {
	if (value instanceof Error) {
		return value.message;
	}

	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'object' && value !== null) {
		try {
			return JSON.stringify(value);
		} catch {
			return String(value);
		}
	}

	return String(value);
};

const normalizeLoggerArgs = (
	message: LoggerMessage
): { error: Error; extras: Record<string, unknown> } => {
	const args = Array.isArray(message.msg) ? message.msg : [message.msg];
	const originalError = args.find((arg): arg is Error => arg instanceof Error);
	const fallbackMessage = args.map(stringifyLoggerArg).filter(Boolean).join(' ');

	return {
		error: originalError ?? new Error(fallbackMessage || 'Logged frontend error'),
		extras: {
			loggerArgs: args.map(serializeSentryExtra),
			loggerMetadata: message.metadata ?? {}
		}
	};
};

const captureSentryError = (message: LoggerMessage): void => {
	if (isCapturingSentryLoggerMessage) {
		return;
	}

	isCapturingSentryLoggerMessage = true;

	try {
		const { error, extras } = normalizeLoggerArgs(message);

		Sentry.withScope((scope) => {
			scope.setExtras(extras);
			Sentry.captureException(error);
		});
	} catch (e) {
		Logger.error('Error capturing Sentry exception', e);
	} finally {
		isCapturingSentryLoggerMessage = false;
	}
};

export const isSentryEnabled = (): boolean => {
	return !isDev() && !isCI();
};

export const InitSentry = (): void => {
	try {
		// Don't send events in development
		if (!isSentryEnabled()) {
			return;
		}

		initSentry({
			dsn: import.meta.env.VITE_SENTRY_DSN,
			integrations: [
				browserTracingIntegration(),
				replayIntegration({
					maskAllText: false,
					blockAllMedia: false
				})
			],
			// Performance Monitoring
			tracesSampleRate: 1.0, //  Capture 100% of the transactions
			// Session Replay
			// eslint-disable-next-line max-len
			replaysSessionSampleRate: 0, // This sets the sample rate at 0%. You may want to change it to 100% while in development and then sample at a lower rate in production.
			// eslint-disable-next-line max-len
			replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
			// eslint-disable-next-line no-undef
			release: `${UI_VERSION}`,
			environment: import.meta.env.VITE_RUN_ENV,
			ignoreErrors: ['Non-Error promise rejection captured'] // https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-1233146122
		});

		setTag('project-id', import.meta.env.VITE_SENTRY_PROJECT_ID);
		setTag('product-name', PRODUCT_NAME);
		Logger.setLogMethod(captureSentryError);
	} catch (error) {
		Logger.error('Error initializing Sentry', error);
	}
};

export const initSentryUser = (user: IUser, tenant: ITenant): void => {
	try {
		const userInfo = {
			email: user.email ?? '',
			role: user.role.name ?? '',
			roleType: user.role.type ?? ''
		};

		const tenantInfo = {
			accountPlan: tenant.accountPlan ?? '',
			companyName: tenant.companyName ?? '',
			id: tenant.id ?? '',
			name: tenant.name ?? ''
		};

		setSentryUser({
			id: user.id,
			username: `${user.name ?? ''} ${user.surname ?? ''}`,
			...tenantInfo,
			...userInfo
		});
	} catch (error) {
		Logger.error('Error initializing Sentry user', error);
	}
};
